/*
 * Buffer that flushes the first message immediately, then emits subsequent messages every <interval> ms
 */
export default class IntervalBuffer<T> {
    private interval = 1000;

    private outIntervalId: NodeJS.Timeout | undefined;
    private pending: T[];

    public constructor(
        /*
         * Callback for outputing messages
         */
        private out: (data: T[]) => void,
        /*
         * Callback to determine whether the interval should be continued
         */
        private continueInterval?: () => boolean
    ) {
        this.out = out;
        this.outIntervalId = undefined;
        this.pending = [];
    }

    public in(data: T[] | undefined | null, suppressImmediateFlush = false) {
        if (!data) {
            return;
        }
        this.pending = this.pending.concat(data);
        this.startInterval(suppressImmediateFlush);
    }

    /*
     * Immediately flush all pending messages
     */
    public flush() {
        this.clearInterval();
        this.flushMessages();
    }

    /*
     * Clears pending messages and stops the interval
     */
    public reset() {
        this.clearInterval();
        this.pending = [];
    }

    /*
     * Flushes pending messages and changes the interval duration
     */
    public setInterval(interval: number) {
        this.interval = interval;
        this.flushMessages();
        this.reset();
    }

    private flushMessages() {
        if (this.pending.length === 0) {
            // No messages to flush. Stop the interval so we flush the next one immediately...
            this.clearInterval();
        }
        if (this.pending.length > 0) {
            this.out(this.pending);
        }
        this.pending = [];
    }

    private startInterval(suppressImmediateFlush = false) {
        if (this.outIntervalId) {
            // Already running...
            return;
        }

        // We flush the first mesage immediately...
        if (!suppressImmediateFlush) {
            this.flushMessages();
        }

        // Then flush every <this.interval> until continueInterval returns false..
        this.outIntervalId = setInterval(() => {
            this.flushMessages();
            if (this.continueInterval && !this.continueInterval()) {
                this.clearInterval();
            }
        }, this.interval);
    }

    private clearInterval() {
        if (this.outIntervalId) {
            clearInterval(this.outIntervalId);
        }
        this.outIntervalId = undefined;
    }
}
